import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormOption } from '@/lib/form-utils';
import { cn } from '@/lib/tailwind-utils';
import { ComponentProps, ReactNode, useRef, useState, useEffect } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

type RadioGroupPropsWithTestId = ComponentProps<typeof RadioGroup> & {
  'data-testid'?: string;
};

interface FormRadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  options: Array<FormOption<string> & { className?: string }>;
  radioGroupProps?: RadioGroupPropsWithTestId;
  className?: string;
  description?: ReactNode;
}

interface RadioGroupFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
  field: {
    value: TFieldValues[TName];
    onChange: (value: TFieldValues[TName] | undefined) => void;
  };
  name: TName;
  label: string;
  options: Array<FormOption<string> & { className?: string }>;
  radioGroupClassName?: string;
  required?: boolean;
  className?: string;
  description?: ReactNode;
  others: Omit<RadioGroupPropsWithTestId, 'onValueChange' | 'value' | 'className'>;
}

const UNCHECK_RESET_DELAY_MS = 100;

function RadioGroupField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  field,
  name,
  label,
  options,
  radioGroupClassName,
  required,
  className,
  description,
  others,
}: RadioGroupFieldProps<TFieldValues, TName>) {
  // Use local state to control the RadioGroup value
  // This allows us to set it to empty string when unchecking
  const [localValue, setLocalValue] = useState<string>(field.value || '');
  const isUncheckingRef = useRef(false);

  // Sync local state with form field value when it changes externally
  useEffect(() => {
    if (!isUncheckingRef.current) {
      setLocalValue(field.value || '');
    }
  }, [field.value]);

  const handleUncheck = () => {
    isUncheckingRef.current = true;
    setLocalValue('');
    field.onChange(undefined);
    setTimeout(() => {
      isUncheckingRef.current = false;
    }, UNCHECK_RESET_DELAY_MS);
  };

  const handlePointerDown = (e: React.PointerEvent, isSelected: boolean) => {
    if (isSelected) {
      e.preventDefault();
      // Don't stop propagation to allow other click handlers to work
      handleUncheck();
    }
  };

  // Extract data-testid from others to use only for individual items, not the container
  const { 'data-testid': dataTestId, ...radioGroupProps } = others;

  return (
    <FormItem className={className}>
      <FormLabel className="font-bold">
        <span>{label.charAt(0).toUpperCase() + label.slice(1)}</span>
        {required && '*'}
      </FormLabel>
      {description && <FormDescription>{description}</FormDescription>}
      <FormControl>
        <RadioGroup
          onValueChange={(value) => {
            // If clicking the same value that's already selected, uncheck it
            if (localValue === value && !isUncheckingRef.current) {
              handleUncheck();
            } else if (!isUncheckingRef.current) {
              setLocalValue(value);
              field.onChange(value as TFieldValues[TName]);
            }
          }}
          value={localValue}
          className={cn('flex flex-wrap gap-4', radioGroupClassName)}
          {...radioGroupProps}
        >
          {options.map((option) => {
            const id = `${name}-${option.value}`;
            const isSelected = localValue === option.value;
            const testId = dataTestId ? `${dataTestId}-${option.value}` : undefined;
            return (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem
                  id={id}
                  value={option.value}
                  onPointerDown={(e) => handlePointerDown(e, isSelected)}
                  data-testid={testId}
                />
                <FormLabel
                  htmlFor={id}
                  className={cn('font-normal', { 'text-gray-400': radioGroupProps.disabled }, option.className)}
                  onPointerDown={(e) => handlePointerDown(e, isSelected)}
                >
                  {option.label}
                </FormLabel>
              </div>
            );
          })}
        </RadioGroup>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}

export function FormRadioGroup<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  options,
  radioGroupProps = {},
  className,
  description,
}: FormRadioGroupProps<TFieldValues, TName>) {
  const { className: radioGroupClassName, required, ...others } = radioGroupProps;

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <RadioGroupField
          field={field}
          name={name}
          label={label}
          options={options}
          radioGroupClassName={radioGroupClassName}
          required={required}
          className={className}
          description={description}
          others={others}
        />
      )}
    />
  );
}
